import React, { useEffect, useState, useContext } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import MobileNav from './MobileNav';
import WindowControlsOverlay from './WindowControlsOverlay';
import DesktopSidebar from './DesktopSidebar';
import DesktopPlayer from './DesktopPlayer';
import MiniPlayer from './MiniPlayer';
import FullPlayer from './FullPlayer';
import { usePlayer } from '../../contexts/PlayerContext';
import { useAuth } from '../../contexts/AuthContext';
import { myAvatar } from '../../utils/me';
import { useAppThemeInit } from '../../hooks/useAppTheme';
import { ListenerThemeProvider } from '../../contexts/ListenerThemeContext';
import { Bell, Flame, UserCircle, Compass } from 'lucide-react';
import useNotifications from '../../contexts/useNotifications';
import { OfflineBanner } from '../../hooks/useOffline';
import ErrorBoundary from '../ErrorBoundary';
import CoursePrompt from '../CoursePrompt';
import { useStreak } from '../../hooks/useStreak';
import { StreakContext } from '../../contexts/StreakContext';
import InstallPrompt from '../InstallPrompt';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import CreateMenuModal from '../CreateMenuModal';
import ListenerCreateMenu from '../ListenerCreateMenu';

const NAV_HEIGHT         = 64;
const MINI_PLAYER_HEIGHT = 64;
const NAV_WITH_PLAYER    = NAV_HEIGHT + MINI_PLAYER_HEIGHT;

// ── Dynamic title map ─────────────────────────────────────────────────────────
const PAGE_TITLES = {
  '/':                    'Home',
  '/browse':              'Browse',
  '/library':             'Library',
  '/library/likes':       'Liked Songs',
  '/library/downloads':   'Downloads',
  '/library/recent':      'Recently Played',
  '/library/following':   'Following',
  '/library/playlists':   'Playlists',
  '/community':           'Community',
  '/hub':                 'Hub',
  '/profile':             'Profile',
  '/profile/edit':        'Edit Profile',
  '/notifications':       'Notifications',
  '/chat':                'Chat',
  '/dashboard':           'Artist Dashboard',
  '/upgrade':             'Upgrade',
  '/about':               'About',
  '/admin':               'Admin',
  '/admin/artists':       'All Artists · Admin',
  '/admin/analytics':     'Analytics · Admin',
  '/admin/moderation':    'Moderation · Admin',
  '/admin/boost':         'Boost Manager · Admin',
  '/admin/broadcast':     'Broadcast · Admin',
  '/admin/behavior':      'User Behavior · Admin',
  '/privacy-policy':      'Privacy Policy',
  '/terms-of-use':        'Terms of Use',
};

const BASE_TITLE = 'Feelz Machine';

function getTitle(pathname) {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (pathname.startsWith('/artist/')) {
    const slug = pathname.split('/artist/')[1]?.split('/')[0];
    if (slug) return slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  if (pathname.startsWith('/album/'))             return 'Album';
  if (pathname.startsWith('/library/playlists/')) return 'Playlist · Library';
  if (pathname.startsWith('/chat/'))              return 'Chat Room';
  const prefix = Object.keys(PAGE_TITLES).find(k => k !== '/' && pathname.startsWith(k + '/'));
  if (prefix) return PAGE_TITLES[prefix];
  return null;
}

// ── Splash screen ─────────────────────────────────────────────────────────────
function SplashScreen() {
  return (
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col items-center justify-center" style={{ left: 0, top: 0, right: 0, bottom: 0 }}>
      <div className="flex flex-col items-center space-y-4">
        <img src="/logo.png" alt="Feelz Machine" className="w-20 h-20 rounded-2xl shadow-2xl" />
        <div className="space-y-1 text-center">
          <p className="text-white font-bold text-xl tracking-tight">Feelz Machine</p>
          <p className="text-white/30 text-xs">Loading...</p>
        </div>
        <div className="flex space-x-1 mt-2">
          {[0, 1, 2].map(i => (
            <div key={i} className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Bell button ───────────────────────────────────────────────────────────────
function MobileBellButton() {
  const { unreadCount } = useNotifications();
  const { streak, discoveryStreak } = useContext(StreakContext);
  const { user, artist, listener, isArtist } = useAuth();
  const navigate = useNavigate();

  const profilePath = isArtist && artist?.slug
    ? `/artist/${artist.slug}`
    : '/profile';

  if (!user) return null;

  return (
    <div
      className="md:hidden fixed top-0 left-0 right-0 z-[60] flex items-center justify-end px-4 pointer-events-none"
      style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 12px)', height: 'calc(max(env(safe-area-inset-top, 0px), 12px) + 44px)' }}
    >
      <div className="flex items-center space-x-1.5 pointer-events-auto">
        {/* Streak flame */}
        {streak > 1 && (
          <div className="flex items-center space-x-1 px-2 py-1 rounded-full bg-orange-500/15 border border-orange-500/25">
            <Flame className="w-3 h-3 text-orange-400" />
            <span className="text-xs font-bold text-orange-400">{streak}</span>
          </div>
        )}
        {/* Discovery streak */}
        {discoveryStreak > 1 && (
          <div className="flex items-center space-x-1 px-2 py-1 rounded-full bg-blue-500/15 border border-blue-500/25">
            <Compass className="w-3 h-3 text-blue-400" />
            <span className="text-xs font-bold text-blue-400">{discoveryStreak}</span>
          </div>
        )}

        {/* Notifications bell */}
        <button
          onClick={() => navigate('/notifications')}
          aria-label="Notifications"
          className="relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/[0.06] transition"
        >
          <Bell className="w-5 h-5 text-white/60" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center text-[9px] font-bold text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {/* Profile shortcut */}
        <button
          onClick={() => navigate(profilePath)}
          aria-label="Profile"
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/[0.06] transition overflow-hidden"
        >
          {/* Was `isArtist ? artist?.profile_image_url : null`, which handed
              every listener a null and then the default icon, however good a
              picture they had uploaded. See src/utils/me.js. */}
          {myAvatar({ artist, listener, user }) ? (
            <img
              src={myAvatar({ artist, listener, user })}
              alt="Profile"
              className="w-full h-full object-cover rounded-full"
            />
          ) : (
            <UserCircle className="w-5 h-5 text-white/60" />
          )}
        </button>
      </div>
    </div>
  );
}

// ── Main layout ───────────────────────────────────────────────────────────────
export default function AppLayout() {
  const { currentTrack, isMinimized }            = usePlayer();
  const { user, artist, loading, isArtist, isBeatmaker, viewAs, setViewAs } = useAuth();
  const location                    = useLocation();
  const [splashDone, setSplashDone] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);

  // Load and apply saved listener theme on startup
  useAppThemeInit();

  // Daily streak, fires ONCE here; all other components read from StreakContext
  const streakValue = useStreak(user);

  // Tour, fires once per account type after first sign-up

  // Splash: wait for auth, then small buffer to avoid flash
  useEffect(() => {
    if (!loading) {
      const t = setTimeout(() => setSplashDone(true), 300);
      return () => clearTimeout(t);
    }
  }, [loading]);

  // THE REDIRECT THAT WAS SENDING EVERYBODY TO /setup IS GONE FROM HERE.
  //
  // There were TWO guards watching the same condition and sending people to
  // two different places:
  //
  //   AppRouter.OnboardingGuard  user with no profile -> /welcome
  //   this one                   user with no profile -> /setup
  //
  // /welcome is the onboarding. /setup is a settings page full of fields. This
  // effect runs on every location change, so in the race between them this one
  // usually won, which means new accounts were being dropped into settings and
  // never saw onboarding at all. That is why signing up appeared to skip it.
  //
  // It was also firing at people who HAVE a profile. hasProfile is
  // `!!artist || !!listener`, computed from two reads that can simply fail. A
  // 503 from Supabase, which your console has been showing, makes both reads
  // come back empty, hasProfile goes false, and a returning account with years
  // of history gets bounced to /setup as though it were brand new. The guard
  // could not tell "this person has no profile" from "we could not load it".
  //
  // OnboardingGuard owns this decision now. It checks the same tables AND
  // hasSeenWelcome(), so somebody who has already been through the flow is let
  // into the app even when the rows do not come back, which is the right
  // failure: a thin profile they can fix in settings beats an account that
  // cannot be used.
  //
  // Nothing replaces it here on purpose. One guard, one destination.

  // Dynamic page titles
  useEffect(() => {
    const title = getTitle(location.pathname);
    document.title = title ? `${title} · ${BASE_TITLE}` : BASE_TITLE;
  }, [location.pathname]);

  // Push notifications, auto-subscribe on load if permission already granted,
  // or prompt if this user follows artists but hasn't granted permission yet.
  const { supported, subscribed, subscribe } = usePushNotifications(user);
  useEffect(() => {
    if (!user || !supported || subscribed || !splashDone) return;
    try {
      if (typeof Notification === 'undefined') return;

      if (Notification.permission === 'granted') {
        // Already granted, re-upsert existing sub in case it went stale
        navigator.serviceWorker.ready.then(reg =>
          reg.pushManager.getSubscription()
        ).then(async (existingSub) => {
          if (existingSub) {
            const { supabase: sb } = await import('../../supabaseClient');
            const p256dh = btoa(String.fromCharCode(...new Uint8Array(existingSub.getKey('p256dh'))));
            const auth   = btoa(String.fromCharCode(...new Uint8Array(existingSub.getKey('auth'))));
            await sb.from('push_subscriptions').upsert(
              { user_id: user.id, endpoint: existingSub.endpoint, p256dh, auth },
              { onConflict: 'user_id,endpoint' }
            );
          } else {
            subscribe().catch(() => {});
          }
        }).catch(() => {});

      }
      // Deliberately no prompt here. Asking on app load gets denied
      // permanently by most people, and the browser gives no second chance.
      // The ask now happens right after a follow, via
      // utils/askNotificationPermission.js, where it connects to something
      // the person just chose to do.
    } catch {}
  }, [user, supported, subscribed, splashDone]);

  const mobilePaddingBottom = currentTrack ? NAV_WITH_PLAYER : NAV_HEIGHT;

  if (!splashDone) return <SplashScreen />;

  return (
    <ListenerThemeProvider>
    <StreakContext.Provider value={streakValue}>
      <div className="min-h-screen bg-black text-white">
        {/* The installed desktop app's own title bar. Renders nothing at all
            unless Window Controls Overlay is actually on, which means a
            browser tab and a phone are untouched by it. */}
        <WindowControlsOverlay />

        {/* Offline detection, fixed banner, renders above everything */}
        <OfflineBanner />

        {/* Listener mode banner */}
        {viewAs === 'listener' && (
          <div className="fixed top-0 inset-x-0 z-[200] flex items-center justify-between px-4 py-2 fm-wco-offset-top"
            style={{ background: 'rgba(139,92,246,0.95)', backdropFilter: 'blur(8px)' }}>
            <p className="text-xs font-semibold text-white">🎧 Listening as fan</p>
            <button onClick={() => setViewAs(null)}
              className="text-xs font-bold text-white/80 hover:text-white transition px-2 py-1 rounded-lg bg-white/20">
              Switch back
            </button>
          </div>
        )}

        <DesktopSidebar />
        <MobileBellButton />

        <main
          className="w-full md:w-[calc(100%-256px)] md:ml-64"
          style={{
            paddingBottom: `calc(${mobilePaddingBottom}px + var(--safe-area-bottom, 0px))`,
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
          }}
        >
          <style>{`
            @media (min-width: 768px) {
              main { padding-bottom: ${currentTrack ? '100px' : '0px'} !important; }
            }
            /* When the docked desktop player is expanded it sits fixed at
               right-0 over 400px. Reserve that space so it pushes content
               aside instead of covering it, otherwise "browse while
               listening" doesn't actually work, the player just hides
               whatever is on the right of the page. */
            @media (min-width: 1024px) {
              main { padding-right: ${currentTrack && !isMinimized ? '400px' : '0px'}; transition: padding-right 0.18s ease; }
            }
          `}</style>
          {/* Waits for the welcome tour to finish, then offers the course
              once. Dismissed for good on the X. See src/components/CoursePrompt.js */}
          <CoursePrompt userId={user?.id} />

          <div className="md:px-8 md:pt-8 w-full">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>

        {location.pathname !== "/" && <FullPlayer />}
        {location.pathname !== "/" && <DesktopPlayer />}
        {location.pathname !== "/" && <MiniPlayer />}
        <MobileNav onOpenCreateMenu={() => setShowCreateMenu(true)} />

        {/* Rendered as a sibling of MobileNav, not a child, MobileNav
            unmounts itself when the on-screen keyboard opens (sensible for
            a bottom nav bar on its own), which was taking this modal down
            with it the instant someone tried to type into it. */}
        {showCreateMenu && (isArtist || isBeatmaker) && (
          <CreateMenuModal
            artist={artist}
            user={user}
            onClose={() => setShowCreateMenu(false)}
          />
        )}
        {showCreateMenu && !isArtist && !isBeatmaker && (
          <ListenerCreateMenu onClose={() => setShowCreateMenu(false)} />
        )}

        {/* The welcome tour is gone. It was thirty slides, sixteen for a
            listener and fourteen for an artist, firing after somebody had
            already answered five questions and before they had got anything
            out of the app. That is where people left.
            The same material now lives in the course at /learn, offered by
            CoursePrompt and reachable from both plus menus and About, so it
            is there when somebody wants it instead of standing between them
            and the music. */}
        <InstallPrompt />
      </div>
    </StreakContext.Provider>
    </ListenerThemeProvider>
  );
}